import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Button,
  Box,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { GroupOutlined } from "@mui/icons-material";
import { Close, FilterList } from "@mui/icons-material";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import RefreshIcon from "@mui/icons-material/Refresh";

const FilterDrawer = ({
  users,
  userContacts,
  loggedInUserId,
  onApplyFilters,
  currentFilters,
  statuses = [],
  loadingStatus = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(currentFilters);

  const todayDDMMYYYY = useMemo(() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }, []);

  const defaultFilters = useMemo(
    () => ({
      userId: loggedInUserId || "all",
      contactId: "all",
      fromDate: todayDDMMYYYY,
      toDate: todayDDMMYYYY,
      statusId: "all",
    }),
    [loggedInUserId, todayDDMMYYYY]
  );

  useEffect(() => {
    if (!currentFilters?.userId) {
      onApplyFilters(defaultFilters);
    }
  }, [defaultFilters, currentFilters, onApplyFilters]);

  const handleApply = () => {
    onApplyFilters(tempFilters);
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempFilters(defaultFilters);
    onApplyFilters(defaultFilters);
    setIsOpen(false);
  };

  const isFilterApplied =
    (currentFilters?.userId ?? defaultFilters.userId) !==
      defaultFilters.userId ||
    (currentFilters?.contactId ?? defaultFilters.contactId) !==
      defaultFilters.contactId ||
    (currentFilters?.statusId ?? defaultFilters.statusId) !==
      defaultFilters.statusId ||
    (currentFilters?.fromDate ?? defaultFilters.fromDate) !==
      defaultFilters.fromDate ||
    (currentFilters?.toDate ?? defaultFilters.toDate) !== defaultFilters.toDate;

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        overflowY: "auto",
      },
    },
  };
  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          color={isFilterApplied ? "secondary" : "primary"}
          onClick={() => {
            setTempFilters(currentFilters);
            setIsOpen(true);
          }}
          sx={{ borderRadius: 3 }}
        >
          Filters
        </Button>
        {isFilterApplied && (
          <Chip label="Applied" color="secondary" size="small" sx={{ ml: 1 }} />
        )}
      </Box>

      <Drawer
        anchor="right"
        open={isOpen}
        hideBackdrop
        ModalProps={{ disableEscapeKeyDown: true }}
        onClose={() => {}}
      >
        <Box sx={{ width: 340, p: 3, position: "relative" }}>
          <IconButton
            onClick={() => setIsOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" mb={3}>
            Filters
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              label="Agent"
              value={tempFilters.userId}
              onChange={(e) =>
                setTempFilters((p) => ({ ...p, userId: e.target.value }))
              }
              MenuProps={menuProps}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="all">
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                    <GroupOutlined fontSize="small" />
                  </Avatar>
                  All Agents
                </Box>
              </MenuItem>

              {users?.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box display="flex" alignItems="center">
                    <Avatar
                      src={user.image_url}
                      alt={user.username}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    {user.username}
                    {user.id === loggedInUserId && (
                      <Chip
                        label="You"
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Contact</InputLabel>
            <Select
              label="Contact"
              value={tempFilters.contactId}
              onChange={(e) =>
                setTempFilters((p) => ({ ...p, contactId: e.target.value }))
              }
              MenuProps={menuProps}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="all">All Contacts</MenuItem>
              {userContacts?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.company || c.first_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={tempFilters.statusId ?? ""}
              onChange={(e) =>
                setTempFilters((p) => ({ ...p, statusId: e.target.value }))
              }
              MenuProps={menuProps}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              {loadingStatus ? (
                <MenuItem disabled>Loading…</MenuItem>
              ) : (
                statuses?.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <BSDateRangePicker
              triggerDate={false}
              showToday={true}
              includeCurrentDate={true}
              allowFutureDate={true}
              fromDate={tempFilters.fromDate}
              toDate={tempFilters.toDate}
              handleStartDate={(from, to) =>
                setTempFilters((p) => ({ ...p, fromDate: from, toDate: to }))
              }
            />
          </Box>

          <Box display="flex" gap={1} mt={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleApply}
              sx={{
                backgroundColor: "#84DEB8",
                color: "#000",
                "&:hover": { backgroundColor: "#6cc9a1" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FilterDrawer;
